import Box from '~/common/components/Box';
import Flex from '~/common/components/Flex';
import { Table, Tbody, Td, Tr } from '~/common/components/Tables';
import Text from '~/common/components/Texts/Text';
import type { WvoeCalculationResult } from '~/pages/IncomeCalculator/types';

import RecentRaiseYearSection from './RecentRaiseYearSection';

export type AdditionalCalculationDetails =
  WvoeCalculationResult['annualDetails'][number]['calculationResults'][number]['additionalCalculationDetails'];

const LpaZeroToTenFormulaType =
  'RecentRaiseLpaQualifyingIncomeZeroToTenPercent';
const LpaAboveTenFormulaType = 'RecentRaiseLpaQualifyingIncomeAboveTenPercent';

type RecentRaiseSectionProps = {
  additionalCalculationDetails: AdditionalCalculationDetails;
  fullWidth?: boolean;
};

function hourIncreaseLabel(formulaType?: string | null) {
  if (formulaType === LpaZeroToTenFormulaType) {
    return '0-10% Increase in Hours';
  }

  if (formulaType === LpaAboveTenFormulaType) {
    return 'Over 10% Increase in Hours';
  }

  return null;
}

type TableColumn = {
  label: string;
  value: string;
  key: string;
};

function renderTableRow(columns: TableColumn[], isBold = false) {
  return (
    <Tr>
      {columns.map((column) => (
        <Td
          key={column.key}
          sx={{
            fontSize: isBold ? '1.125rem' : '1rem',
            fontWeight: isBold ? 'bold' : 'normal',
            borderBottom: 'none',
            py: '0.25rem',
          }}
        >
          {isBold ? column.value : column.label}
        </Td>
      ))}
    </Tr>
  );
}

export default function RecentRaiseSection({
  additionalCalculationDetails,
  fullWidth = false,
}: Readonly<RecentRaiseSectionProps>) {
  const {
    recentRaiseDetailResponsesByIncomeYear,
    calculatedMonthlyQualifyingAmount,
    formula,
    formulaDisplayString,
    formulaType,
    additionalCalculationFormula,
    dateOfRaise,
    priorRate,
    currentRate,
    throughDateUsedForCalculation,
  } = additionalCalculationDetails;

  const tableColumns: TableColumn[] = [
    {
      label: 'Date Raise Received',
      value: dateOfRaise,
      key: 'date-raise-received',
    },
    { label: 'Prior Rate', value: priorRate, key: 'prior-rate' },
    { label: 'Current Rate', value: currentRate, key: 'current-rate' },
  ];
  const lpaHourIncreaseLabel = hourIncreaseLabel(formulaType);
  const isLpaRecentRaise = lpaHourIncreaseLabel != null;

  return (
    <Box width="100%">
      <Box mb={4} maxW={fullWidth ? '100%' : '31%'}>
        <Table sx={{ tr: { height: '28px' } }}>
          <Tbody>
            {renderTableRow(tableColumns, false)}
            {renderTableRow(tableColumns, true)}
          </Tbody>
        </Table>
      </Box>

      <Flex gap={4} flexWrap="wrap" alignItems="flex-start">
        {recentRaiseDetailResponsesByIncomeYear?.map((yearDetail) => (
          <Box
            flex="1"
            minW={fullWidth ? '250px' : '300px'}
            maxW={fullWidth ? '100%' : '35%'}
            key={`${yearDetail.incomeYear}`}
          >
            <RecentRaiseYearSection
              {...yearDetail}
              throughDateUsedForCalculation={throughDateUsedForCalculation}
              isLpaRecentRaise={isLpaRecentRaise}
            />
          </Box>
        ))}
      </Flex>

      {isLpaRecentRaise ? (
        <Box mt={5}>
          {additionalCalculationFormula?.map((calculationFormula) => (
            <Box key={calculationFormula.formulaName} mb={4}>
              <Text fontWeight="bold" mt={5} mb={2}>
                Change in Monthly Hours from Last Year to YTD
              </Text>
              <Text mb={1} color="gray.600">
                = {calculationFormula.formulaName}
              </Text>
              <Text mb={1} color="gray.600">
                = {calculationFormula.formula}
              </Text>
              <Text mb={4}>= {calculationFormula.formulaHours}</Text>
            </Box>
          ))}

          <Flex alignItems="center" mb={2}>
            <Text fontWeight="bold">Qualifying Income</Text>
            <Text
              ml={2}
              px={2}
              py={1}
              fontSize="sm"
              bg="gray.100"
              borderRadius="md"
              color="gray.700"
            >
              {lpaHourIncreaseLabel}
            </Text>
          </Flex>
          <Text mb={1} color="gray.600">
            = {formulaDisplayString}
          </Text>
          <Text mb={1} color="gray.600">
            = {formula}
          </Text>
          <Text mb={1} fontWeight="bold" fontSize="lg">
            = {calculatedMonthlyQualifyingAmount}
          </Text>
        </Box>
      ) : (
        <Box mt={5}>
          <Text fontWeight="bold" mt={5} mb={2}>
            Income at Current Rate
          </Text>
          <Text mb={1}>= {formulaDisplayString}</Text>
          <Text mb={1}>= {formula}</Text>
          <Text mb={1} fontWeight="bold">
            = {calculatedMonthlyQualifyingAmount} / month
          </Text>
        </Box>
      )}
    </Box>
  );
}
